import { Crepe } from '@milkdown/crepe';
import { commandsCtx } from '@milkdown/kit/core';
import { useInstance } from '@milkdown/react';
import React from 'react';
import './CardToolbar.css';

interface CardTemplate {
  type: string;
  label: string;
  emoji: string;
  template?: string;
  command?: string; // NEW: Command name to execute
  icon?: string;
}

const CARD_TEMPLATES: CardTemplate[] = [
  {
    type: 'win',
    label: 'Win',
    emoji: '✅',
    command: 'insertCard',
    icon: 'check_circle',
  },
  {
    type: 'warning',
    label: 'Warning',
    emoji: '⚠️',
    command: 'insertCard',
    icon: 'warning',
  },
  {
    type: 'quote',
    label: 'Quote',
    emoji: '💬',
    command: 'insertCard',
    icon: 'format_quote',
  },
  {
    type: 'note',
    label: 'Note',
    emoji: '🗒️',
    command: 'insertCard',
    icon: 'note',
  },
  {
    type: 'grouped',
    label: 'Group Cards',
    emoji: '📊',
    command: 'InsertGroupedCards',
    icon: 'view_column',
  },
];

export interface CardToolbarProps {
  className?: string;
}

/**
 * CardToolbar provides buttons to insert card nodes into a Milkdown editor.
 *
 * Uses ProseMirror commands to insert cards at the cursor position:
 * - Single cards (win, warning, quote, note): insertCard command
 * - Grouped cards: InsertGroupedCards command
 */
export const CardToolbar: React.FC<CardToolbarProps> = ({ className }) => {
  const [loading, get] = useInstance();

  const insertCard = React.useCallback(
    (card: CardTemplate) => {
      if (loading) return;

      const crepe = get() as unknown as Crepe | undefined;
      if (!crepe) {
        console.warn('CardToolbar: Crepe instance not available');
        return;
      }

      try {
        const command = card.command;
        if (!command) {
          console.warn('CardToolbar: No command specified for card type', card.type);
          return;
        }

        crepe.editor.action((ctx) => {
          const commandManager = ctx.get(commandsCtx);

          // For single cards, pass the cardType as parameter
          if (command === 'insertCard') {
            commandManager.call(command, card.type);
          } else {
            // For other commands (like grouped cards), call without parameters
            commandManager.call(command);
          }
        });
      } catch (error) {
        console.error('CardToolbar: Failed to insert card', error);
      }
    },
    [loading, get]
  );

  return (
    <div className={`card-toolbar ${className || ''}`}>
      <div className="card-toolbar-label">Insert Card:</div>
      <div className="card-toolbar-buttons">
        {CARD_TEMPLATES.map((card) => (
          <button
            key={card.type}
            type="button"
            className={`card-toolbar-button card-toolbar-button-${card.type}`}
            onClick={() => insertCard(card)}
            title={`Insert ${card.label} card`}
            disabled={loading}
          >
            <span className="card-toolbar-button-emoji">{card.emoji}</span>
            <span className="card-toolbar-button-label">{card.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};
